import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaEnvelope, FaUserEdit, FaUserShield } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import ChangeUserRole from '../components/ChangeUserRole';
import Pagination from '../components/Pagination';
import { request } from '../helpers/request';

const AllUsers = () => {
    const [allUser, setAllUsers] = useState([])
    const [openUpdateRole, setOpenUpdateRole] = useState(false)
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pages: 1,
        limit: 20
    });
    const [updateUserDetails, setUpdateUserDetails] = useState({
        email: "",
        name: "",
        role: "",
        _id: ""
    })

    const fetchAllUsers = async (page = 1) => {
        try {
            const response = await request({
                ...SummaryApi.allUser,
                url: `${SummaryApi.allUser.url}?page=${page}&limit=20`
            })
            const dataResponse = await response.json()

            if (dataResponse.success) {
                setAllUsers(dataResponse.data)
                setPagination(dataResponse.pagination)
            }

            if (dataResponse.error) {
                toast.error(dataResponse.message)
            }
        } catch (error) {
            toast.error("Failed to fetch users")
        }
    }

    useEffect(() => {
        fetchAllUsers()
    }, [])

    const handlePageChange = (newPage) => {
        fetchAllUsers(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className='p-2 md:p-0 space-y-6 md:space-y-8'>
            <div className='flex flex-col md:flex-row md:items-end justify-between gap-6'>
                <div className='space-y-1.5 md:space-y-2'>
                    <h1 className='text-3xl md:text-4xl font-black text-primary tracking-tight'>Manage Access</h1>
                    <p className='text-[11px] md:text-sm text-gray-400 font-black uppercase tracking-widest'>Control system permissions</p>
                </div>
                <div className='bg-white px-5 py-4 rounded-[1.5rem] md:rounded-3xl border border-surface-border shadow-sm flex items-center gap-4 w-fit'>
                    <div className='w-9 h-9 md:w-10 md:h-10 bg-accent/10 rounded-xl flex items-center justify-center text-accent'>
                        <FaUserShield className="text-sm md:text-base" />
                    </div>
                    <div>
                        <p className='text-[8px] md:text-[10px] font-black uppercase tracking-widest text-gray-400'>Total Users</p>
                        <p className='text-lg md:text-xl font-black text-primary leading-tight'>{pagination.total}</p>
                    </div>
                </div>
            </div>

            {/* Desktop Table View */}
            <div className='hidden lg:block bg-white rounded-[2.5rem] shadow-premium border border-surface-border overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-left'>
                        <thead>
                            <tr className='bg-surface-muted/50 border-b border-surface-border'>
                                <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>User Profile</th>
                                <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>Contact Info</th>
                                <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>Access Level</th>
                                <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400'>Member Since</th>
                                <th className='px-8 py-6 text-xs font-black uppercase tracking-widest text-gray-400 text-center'>Actions</th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-surface-border'>
                            {allUser.map((user) => (
                                <tr key={user._id} className='group hover:bg-surface-muted/30 transition-colors'>
                                    <td className='px-8 py-6'>
                                        <div className='flex items-center gap-4'>
                                            <div className='w-12 h-12 bg-surface-muted rounded-full flex items-center justify-center text-primary font-black uppercase border border-surface-border group-hover:bg-white transition-colors'>
                                                {user?.name?.charAt(0) || 'U'}
                                            </div>
                                            <div>
                                                <p className='font-bold text-primary capitalize'>{user?.name}</p>
                                                <p className='text-[10px] text-gray-400 font-black uppercase tracking-widest'>{user?._id?.slice(-8)}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className='px-8 py-6'>
                                        <div className='flex flex-col gap-1'>
                                            <div className='flex items-center gap-2 text-sm text-gray-600 font-medium'>
                                                <FaEnvelope size={12} className='text-gray-400' />
                                                <span>{user?.email}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className='px-8 py-6'>
                                        <span className={`
                                            px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border
                                            ${user?.role === 'ADMIN' 
                                                ? 'bg-accent/10 text-accent border-accent/20' 
                                                : 'bg-primary/5 text-primary border-primary/10'}
                                        `}>
                                            {user?.role}
                                        </span>
                                    </td>
                                    <td className='px-8 py-6'>
                                        <p className='text-sm text-gray-500 font-medium'>{moment(user?.createdAt).format('MMM Do, YYYY')}</p>
                                    </td>
                                    <td className='px-8 py-6'>
                                        <div className='flex justify-center'>
                                            <button 
                                                onClick={() => {
                                                    setUpdateUserDetails(user)
                                                    setOpenUpdateRole(true)
                                                }}
                                                className='w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-gray-400 hover:bg-primary hover:text-white transition-all shadow-sm'
                                                title="Edit Permissions"
                                            >
                                                <FaUserEdit size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile Card View */}
            <div className='lg:hidden space-y-4'>
                {allUser.map((user) => (
                    <div key={user._id} className='bg-white rounded-3xl p-6 border border-surface-border shadow-sm'>
                        <div className='flex items-center justify-between mb-6'>
                            <div className='flex items-center gap-4'>
                                <div className='w-12 h-12 bg-surface-muted rounded-full flex items-center justify-center text-primary font-black uppercase border border-surface-border'>
                                    {user?.name?.charAt(0) || 'U'}
                                </div>
                                <div>
                                    <p className='font-bold text-primary capitalize'>{user?.name}</p>
                                    <p className='text-[10px] text-gray-400 font-black uppercase tracking-widest'>{user?._id?.slice(-8)}</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => {
                                    setUpdateUserDetails(user)
                                    setOpenUpdateRole(true)
                                }}
                                className='w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-primary'
                            >
                                <FaUserEdit size={16} />
                            </button>
                        </div>

                        <div className='grid grid-cols-2 gap-4 pt-4 border-t border-surface-border/50'>
                            <div className='space-y-1'>
                                <p className='text-[10px] font-black uppercase tracking-widest text-gray-400'>Access</p>
                                <span className={`
                                    inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border
                                    ${user?.role === 'ADMIN' 
                                        ? 'bg-accent/10 text-accent border-accent/20' 
                                        : 'bg-primary/5 text-primary border-primary/10'}
                                `}>
                                    {user?.role}
                                </span>
                            </div>
                            <div className='space-y-1'>
                                <p className='text-[10px] font-black uppercase tracking-widest text-gray-400'>Joined</p>
                                <p className='text-xs font-bold text-gray-600'>{moment(user?.createdAt).format('MMM D, YYYY')}</p>
                            </div>
                        </div>

                        <div className='mt-4 flex items-center gap-2 text-xs text-gray-500 font-medium bg-surface-muted/50 p-3 rounded-xl'>
                            <FaEnvelope size={10} className='text-gray-400 flex-shrink-0' />
                            <span className='truncate'>{user?.email}</span>
                        </div>
                    </div>
                ))}
            </div>

            <Pagination 
                page={pagination.page} 
                pages={pagination.pages} 
                onPageChange={handlePageChange} 
            />

            {openUpdateRole && (
                <ChangeUserRole 
                    onClose={() => setOpenUpdateRole(false)} 
                    name={updateUserDetails.name}
                    email={updateUserDetails.email}
                    role={updateUserDetails.role}
                    userId={updateUserDetails._id}
                    callFunc={fetchAllUsers}
                />
            )}
        </div>
    )
}

export default AllUsers